import * as t from "io-ts";
import * as util from "../../shared/src/util";

// The vocabulary the valuation engine prices on, plus `unknown` (the engine has no
// such state, and "nobody has said" has to stay distinct from "average" — the engine
// records hasCondition from it).
//
// The last three are SUPERSEDED. They are kept here because rows in the database still
// hold them and a codec that stops accepting a stored value is an outage — this repo
// has done it twice, with the roadblock literal rename (#3447) and the PIF strict
// codec. They are absent from `selectableValues`, so they cannot be chosen again, and
// a listing keeps its old label until someone re-values the property and says what it
// really is. Deliberately not migrated: nobody was ever asked whether a "good" property
// was excellent or good, so moving it would invent the answer at 1.1x versus 1.0x.
export const codec = t.union([
    t.literal("unknown"),
    t.literal("excellent"),
    t.literal("good"),
    t.literal("average"),
    t.literal("poor"),
    t.literal("refurb_required"),
    t.literal("major_renovation_required"),
    t.literal("good_benefit_from_refurbishment"),
    t.literal("poor_liveable_but_requires_work"),
    t.literal("poor_not_immediately_liveable"),
]);

export const values = util.typeValuesFromCodec(codec.types as Array<t.LiteralC<T>>);

type T = t.TypeOf<typeof codec>;

// What a valuer can choose today. The superseded values are absent on purpose: they
// still decode, so existing rows render, but nothing new can be filed under them.
export const selectableValues: Array<T> = [
    "unknown",
    "excellent",
    "good",
    "average",
    "poor",
    "refurb_required",
    "major_renovation_required",
];

// What to offer in a dropdown for a listing currently holding `current`.
//
// The selectable values, plus the one the listing already has if that one has been
// superseded — otherwise a property whose condition IS recorded renders blank, which
// reads as missing data rather than as an old answer.
//
// A function rather than a rule each dropdown reimplements: the enquiry form offered
// all ten values for a while because it used `values` directly, so a valuer could file
// a brand-new listing under a retired answer. One definition means the next dropdown
// cannot get it wrong.
export const optionsFor = (current: T | null): Array<T> =>
    current === null || selectableValues.includes(current)
        ? selectableValues
        : [...selectableValues, current];

export const toDisplayName = (p: T): string => {
    switch (p) {
        case "unknown":
            return "Unknown";
        case "excellent":
            return "Excellent";
        case "good":
            return "Good";
        case "average":
            return "Average";
        case "poor":
            return "Poor";
        case "refurb_required":
            return "Refurbishment required";
        case "major_renovation_required":
            return "Major renovation required";
        // Superseded, shown only where a listing still holds one. Labelled so a valuer
        // can see it is an old answer rather than one of today's options.
        case "good_benefit_from_refurbishment":
            return "Good, would benefit from refurbishment (old)";
        case "poor_liveable_but_requires_work":
            return "Poor, liveable but required work (old)";
        case "poor_not_immediately_liveable":
            return "Poor, not immediately liveable (old)";
        default:
            return util.requireExhaustive(p);
    }
};
